import { useState } from 'react';
import * as R from 'ramda';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { itemsInStack, MapItemList } from '@atom/map';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import BulkCreateModal from '../bulkCreate/BulkCreateModal';
import BulkLimitModal from '../bulkCreate/BulkLimitModal';
import { useMapPortalData } from '../MapPortalDataProvider';

const ListView = () => {
  const { data, hasRegion, excludedCount, excludeAsset } = useMapPortalData();
  const preferences = usePreferences();

  const [{ stack, stackType, id }, { updateSearchParams }] = useNavigation({
    stack: SearchParamType.STRING,
    stackType: SearchParamType.STRING,
    id: SearchParamType.ARRAY,
  });

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);

  const { assets = [], workOrders = [] } = data;

  const enableBulkCreate: boolean = R.pathOr(
    false,
    ['workOrders', 'createBulkWorkFromMap'],
    preferences,
  );
  const showBulkCreateButton = hasRolePermissions(ROLE_SETS.MANAGER);

  // A stack originates from a single group marker (asset or work order), so
  // resolve membership against only that category — otherwise a co-located
  // work order and asset stack at the same coordinates would be merged.
  const stackMembers =
    stackType === 'workOrder'
      ? itemsInStack(workOrders, stack ?? '')
      : itemsInStack(assets, stack ?? '');

  const identifyIds: string[] | null = stack
    ? stackMembers.map(item => item.id)
    : (id ?? null);

  const handleBulkCreate = () => {
    if (assets.length > 5000) {
      setLimitModalOpen(true);
    } else {
      setCreateModalOpen(true);
    }
  };

  return (
    <>
      <MapItemList
        data={data}
        identifyIds={identifyIds}
        onSelectAsset={selectedId =>
          updateSearchParams({
            id: selectedId,
            view: 'asset',
            stack: null,
            stackType: null,
          })
        }
        onSelectWork={selectedId =>
          updateSearchParams({
            id: selectedId,
            view: 'workOrder',
            stack: null,
            stackType: null,
          })
        }
        onSelectUser={selectedId =>
          updateSearchParams({
            id: selectedId,
            view: 'user',
            stack: null,
            stackType: null,
          })
        }
        onHover={hoverId => updateSearchParams({ hover: hoverId })}
        onCloseIdentify={() =>
          updateSearchParams({ stack: null, stackType: null, id: null })
        }
        bulkCreate={
          enableBulkCreate
            ? { canCreate: showBulkCreateButton, onCreate: handleBulkCreate }
            : undefined
        }
        exclusion={
          hasRegion ? { onExclude: excludeAsset, excludedCount } : undefined
        }
      />
      <BulkCreateModal
        assets={assets}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      <BulkLimitModal
        open={limitModalOpen}
        onConfirm={() => setLimitModalOpen(false)}
      />
    </>
  );
};

export default ListView;
